import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND } from "../lib/content";
import { CornerWebs } from "./CornerWebs";
import { EditorialMarquee } from "./EditorialMarquee";

const LINKS = [
  { to: "/", label: "Kickstarter" },
  { to: "/home", label: "Home" },
  { to: "/series", label: "Series" },
  { to: "/videos", label: "Videos" },
  { to: "/socials", label: "Socials" },
  { to: "/support", label: "Support" },
  { to: "/tattoo-times", label: "Tattoo Times" },
];

const FlameWord = ({ text }) => (
  <span className="flex items-center justify-center gap-[3px] md:gap-[5px]">
    {text.toUpperCase().split("").map((ch, i) =>
      ch === " " ? (
        <span key={i} className="w-3 md:w-6" />
      ) : (
        <img
          key={i}
          src={`/brand/ta-letter-${ch.toLowerCase()}.png`}
          alt={ch}
          className="h-8 w-auto drop-shadow-[0_0_1px_rgba(0,0,0,0.4)] transition duration-300 group-hover:brightness-110 group-hover:drop-shadow-[0_0_18px_rgba(255,122,26,0.6)] md:h-[3.4rem]"
        />
      )
    )}
  </span>
);

export const SiteMenu = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      window.lenis?.stop();
      const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
        window.lenis?.start();
      };
    }
    document.body.style.overflow = "";
    window.lenis?.start();
  }, [open]);

  return (
    <>
      <button
        data-testid="nav-menu-toggle"
        onClick={() => setOpen((v) => !v)}
        className="text-white"
        aria-label="Menu"
      >
        {open ? <X size={26} /> : <Menu size={26} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[60] flex h-[100dvh] w-screen flex-col overflow-hidden bg-ink"
          >
            <div className="pointer-events-none absolute left-1/2 top-1/3 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-flame opacity-[0.16] blur-[150px]" />
            <div className="pointer-events-none absolute -right-16 bottom-0 h-[380px] w-[380px] rounded-full bg-crimson opacity-[0.12] blur-[150px]" />
            <CornerWebs />

            <div className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-4 md:px-10">
              <Link to="/home" data-testid="mobile-menu-logo" onClick={() => setOpen(false)} className="flex items-center gap-3">
                <img src={BRAND.initials} alt="Tattooed Abroad" className="h-9 w-auto md:h-11" />
              </Link>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-white transition-colors hover:text-flame"
                aria-label="Close menu"
              >
                <X size={26} />
              </button>
            </div>

            <EditorialMarquee gap="mt-0" />

            <div className="relative z-10 flex flex-1 items-center justify-center gap-6 px-6 md:gap-16">
              <img src={BRAND.star} alt="" className="hidden h-20 w-20 shrink-0 opacity-70 md:block lg:h-28 lg:w-28" />

              <div className="flex flex-col items-center gap-1">
                {LINKS.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={l.to}
                      onClick={() => setOpen(false)}
                      aria-label={l.label}
                      className="group block py-2.5"
                    >
                      <FlameWord text={l.label} />
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 + LINKS.length * 0.07, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to="/home#join"
                    onClick={() => setOpen(false)}
                    data-testid="menu-join-cta"
                    className="mt-8 inline-block border border-flame px-8 py-3.5 text-center font-body text-xs uppercase tracking-[0.25em] text-white transition-colors hover:bg-flame hover:text-ink"
                  >
                    Join the List
                  </Link>
                </motion.div>
              </div>

              <img src={BRAND.star} alt="" className="hidden h-20 w-20 shrink-0 opacity-70 md:block lg:h-28 lg:w-28" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
